import { Control, Controller, FieldValues, Path, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { capitalizeFirstLetter } from '@/util/util';

import Webview from './CarouselWebview';

interface DefaultActionProps<FormType extends FieldValues> {
  updateRedux: () => void;
  actionId: string;
  index?: number;
  cardIndex?: number;
  control: Control<FormType>;
}

const CarouselDefaultAction = <FormType extends FieldValues>({
  updateRedux,
  actionId,
  index,
  cardIndex,
  control,
}: DefaultActionProps<FormType>) => {
  const { t } = useTranslation();

  const type = useWatch({ control, name: 'type' as Path<FormType> });

  switch (type) {
    case 'url': {
      return (
        <Controller
          control={control}
          name={'url' as Path<FormType>}
          render={({ field, fieldState: { error } }) => (
            <>
              <Input
                {...field}
                value={field.value ?? ''}
                onChange={(event) => {
                  field.onChange(event);
                  updateRedux();
                }}
                placeholder={t('dialog.carousel.url_placeholder')}
                aria-invalid={!!error}
              />
              {error && (
                <p className="text-sm text-warning">{capitalizeFirstLetter(error.message)}</p>
              )}
            </>
          )}
        />
      );
    }

    case 'phone':
    case 'postback': {
      return (
        <Controller
          control={control}
          name={'value' as Path<FormType>}
          render={({ field, fieldState: { error } }) => (
            <>
              <Input
                {...field}
                value={field.value ?? ''}
                onChange={(event) => {
                  field.onChange(event);
                  updateRedux();
                }}
                placeholder={t(`dialog.carousel.${type}_placeholder`)}
                aria-invalid={!!error}
              />
              {error && (
                <p className="text-sm text-warning">{capitalizeFirstLetter(error.message)}</p>
              )}
            </>
          )}
        />
      );
    }
    case 'webview':
      return (
        <Webview
          key={`${actionId}-webview`}
          actionId={actionId}
          updateRedux={updateRedux}
          control={control}
          index={index}
          cardIndex={cardIndex}
        />
      );
    default:
      return null;
  }
};

export default CarouselDefaultAction;
